import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { WizardStep, WizardActions } from '@/components/WizardShell';
import PanelLayout from '@/components/PanelLayout';
import { Progress } from '@/components/ui/progress';
import ImageUploader from '@/components/ImageUploader';
import { ModerationInfoBox } from '@/components/ModerationStatus';
import { useFunnelTracking } from '@/hooks/useFunnelTracking';
import { useUnsavedChanges, useAutosaveDraft } from '@/hooks/useUnsavedChanges';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, MapPin, Phone, FileText, Image as ImageIcon, 
  Dog, Cat, Sparkles, Loader2, Check, Eye, Heart, AlertTriangle, Share2, X, Crown,
  TrendingUp, AlertCircle, CheckCircle2, Camera, Home, Copy, Palette, Sun, Moon, Shield, Save, Search
} from 'lucide-react';
import { firePawConfetti } from '@/utils/confetti';
import { getVoivodeshipFromPostalCode, formatPostalCode, isValidPostalCode } from '@/utils/postalCode';
import ShareActions from '@/components/ShareActions';

import { API } from '@/utils/api';

// ==================== PERSONALIZATION CONSTANTS ====================
const ACCENT_COLORS_CONFIG = [
  { id: 'gold', labelKey: 'colors.gold', hex: '#c9a96e' },
  { id: 'blue', labelKey: 'colors.blue', hex: '#6e8ec9' },
  { id: 'green', labelKey: 'colors.green', hex: '#6ec98a' },
  { id: 'red', labelKey: 'colors.red', hex: '#c96e6e' },
  { id: 'purple', labelKey: 'colors.purple', hex: '#8e6ec9' },
  { id: 'teal', labelKey: 'colors.teal', hex: '#6ec9b8' },
  { id: 'pastel-pink', labelKey: 'colors.pastelPink', hex: '#f4b8c1' },
  { id: 'pastel-blue', labelKey: 'colors.pastelBlue', hex: '#a8c8e8' },
  { id: 'pastel-mint', labelKey: 'colors.pastelMint', hex: '#a8dbc5' },
  { id: 'pastel-lavender', labelKey: 'colors.pastelLavender', hex: '#c5b8d9' },
  { id: 'pastel-peach', labelKey: 'colors.pastelPeach', hex: '#f4c9a8' },
  { id: 'pastel-yellow', labelKey: 'colors.pastelYellow', hex: '#f0e0a0' },
];

const TEMPLATE_OPTIONS = [
  { id: 'v1', label: 'Klasyczny', desc: 'Sprawdzony układ z prostą nawigacją' },
  { id: 'v3', label: 'Premium', desc: 'Nowoczesny design z animacjami i sekcjami' },
];

const DEFAULT_SECTIONS = ['o-hodowli', 'psy', 'mioty', 'galeria', 'kontakt'];

// ==================== CORE CREATOR IMPORTS ====================
import { validateStep as coreValidateStep, validateAllMust as coreValidateAllMust } from '@/creators/core/validation';
import { computeQualityScore, getStatusConfig, QUALITY_THRESHOLDS } from '@/creators/core/qualityScore';
import { kennelProfileSchema, kennelSteps, hasContactMethod } from '@/creators/schemas/kennel.profile.schema';
import { hasContactMethod as checkContact } from '@/creators/profiles/fieldMapping';

// ==================== CTA CONTACT VALIDATION ====================
const CTA_CONTACT_WARNING_KEY = "kennelWizard.contactWarning";

// ==================== IMAGE REQUIREMENTS ====================
const IMAGE_REQUIREMENTS = {
  cover_image: {
    recommended: true,
    minWidth: 1200,
    minHeight: 630,
    helpTextKey: "kennelWizard.photosHint"
  },
  gallery: {
    recommendedCount: 4,
    maxCount: 10,
    helpTextKey: "kennelWizard.photosHint"
  }
};

// ==================== QUALITY SCORE BAR COMPONENT ====================
function QualityScoreBar({ formData, onMissingClick }) {
  const { t } = useTranslation();
  // Use new core quality score
  const scoreResult = computeQualityScore({ 
    entityType: 'PROFILE', 
    formData, 
    schema: kennelProfileSchema 
  });
  const { score, status, suggestions, missingMust } = scoreResult;
  const config = getStatusConfig(status);
  
  const StatusIcons = {
    red: AlertCircle,
    yellow: TrendingUp,
    green: CheckCircle2
  };
  const Icon = StatusIcons[status];
  
  // Check for contact method warning
  const hasContact = hasContactMethod(formData);
  const showContactWarning = !hasContact && score > 30; // Show after basic info filled
  
  // Combine suggestions (prioritize MUST missing, then SHOULD)
  const displaySuggestions = [
    ...missingMust.slice(0, 2),
    ...suggestions.slice(0, 3 - Math.min(2, missingMust.length))
  ];

  return (
    <div className={`rounded-xl border ${config.border} ${config.bgLight} p-4 mb-6`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${config.color}`} />
          <span className={`font-medium ${config.color}`}>
            {t('kennelWizard.qualityScore')}: {score}%
          </span>
          <span className={`text-xs ${config.color} opacity-70`}>
            ({config.label})
          </span>
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="h-2 bg-zinc-200 dark:bg-gray-700 rounded-full overflow-hidden mb-3">
        <motion.div 
          className={`h-full ${config.bg} rounded-full`}
          initial={{ width: 0 }}
          animate={{ width: `${score}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>
      
      {/* Contact method warning */}
      {showContactWarning && (
        <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 text-xs mb-3 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
          <Phone className="w-4 h-4" />
          <span>{t(CTA_CONTACT_WARNING_KEY)}</span>
        </div>
      )}
      
      {/* Missing suggestions */}
      {displaySuggestions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-zinc-500">{t('kennelWizard.addMore')}</span>
          {displaySuggestions.map((item) => (
            <button
              key={item.field}
              onClick={() => onMissingClick?.(item)}
              className="text-xs px-2 py-1 bg-white dark:bg-gray-800 border border-zinc-200 dark:border-gray-600 rounded-full hover:border-[#F47A3B] hover:text-[#F47A3B] transition-colors"
            >
              + {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== TYPY STRON DLA HODOWCÓW ====================
const INDUSTRIES = {
  hodowla_psow: {
    id: 'hodowla_psow',
    name: 'Hodowla psów',
    icon: Dog,
    emoji: '🐕',
    description: 'Profesjonalna hodowla psów rasowych',
    color: 'amber',
    showSpecies: true,
    labels: {
      name: 'Nazwa hodowli',
      namePlaceholder: 'np. Złota Łapa FCI',
      description: 'Opis hodowli',
      cta: 'Skontaktuj się'
    }
  },
  hodowla_kotow: {
    id: 'hodowla_kotow',
    name: 'Hodowla kotów',
    icon: Cat,
    emoji: '🐱',
    description: 'Profesjonalna hodowla kotów rasowych',
    color: 'purple',
    showSpecies: true,
    labels: {
      name: 'Nazwa hodowli',
      namePlaceholder: 'np. Royal Cats',
      description: 'Opis hodowli',
      cta: 'Skontaktuj się'
    }
  },
  inne: {
    id: 'inne',
    name: 'Inna hodowla',
    icon: Home,
    emoji: '🏠',
    description: 'Hodowla innych zwierząt',
    color: 'slate',
    labels: {
      name: 'Nazwa',
      namePlaceholder: 'np. Nazwa firmy',
      description: 'Opis',
      cta: 'Skontaktuj się'
    }
  }
};

// Breed lists
import { DOG_BREEDS, CAT_BREEDS } from '@/data/breeds';

const voivodeships = [
  'dolnośląskie', 'kujawsko-pomorskie', 'lubelskie', 'lubuskie',
  'łódzkie', 'małopolskie', 'mazowieckie', 'opolskie',
  'podkarpackie', 'podlaskie', 'pomorskie', 'śląskie',
  'świętokrzyskie', 'warmińsko-mazurskie', 'wielkopolskie', 'zachodniopomorskie',
];

const organizations = [
  { id: 'zkwp_fci', label: 'ZKwP (FCI)', fci: true },
  { id: 'fci_other', label: 'Inna organizacja FCI', fci: true },
  { id: 'khron', label: 'KHRON', fci: false },
  { id: 'wush', label: 'WUSH', fci: false },
  { id: 'other', label: 'Inna organizacja', fci: false },
  { id: 'unknown', label: 'Brak / nie podano', fci: false },
];

export default function KennelWizard() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const redirectToMiot = searchParams.get('then') === 'miot';
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  // Funnel tracking
  const { trackStart, trackStep, trackComplete, trackError } = useFunnelTracking('kennel_wizard');
  
  // Step 0 = industry selection (only for new sites)
  const [step, setStep] = useState(isEdit ? 1 : 0);
  const [industry, setIndustry] = useState('hodowla_psow'); // Default to hodowla psów
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(isEdit); // Loading state for edit mode
  const [generating, setGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showSharePanel, setShowSharePanel] = useState(false);
  const [savedKennelId, setSavedKennelId] = useState(null);
  const [savedKennelSlug, setSavedKennelSlug] = useState(null);
  const [originalData, setOriginalData] = useState(null);

  // Get current industry config
  const industryConfig = INDUSTRIES[industry] || INDUSTRIES.inne;

  const [form, setForm] = useState({
    name: '',
    industry: 'hodowla',
    species: [],
    breeds: [],
    postal_code: '',
    city: '',
    street: '',
    house_number: '',
    voivodeship: '',
    country: 'Polska',
    organization: '',
    organization_custom: '',
    organization_type: 'unknown',
    is_fci: false,
    registration_number: '',
    branch_name: '',
    founded_year: '',
    email: '',
    phone: '',
    facebook: '',
    instagram: '',
    website: '',
    description: '',
    cover_image: '',
    gallery: [],
    theme: 'dark',
    accent_color: 'gold',
    template_version: 'v1',
    section_order: DEFAULT_SECTIONS,
    logo: '',
    show_in_catalog: false,
  });

  // Unsaved changes tracking
  const { hasChanges: hasUnsavedChanges, markAsSaved } = useUnsavedChanges(
    form, 
    originalData, 
    !showSuccessModal // Disable when success modal shown
  );

  // Auto-save gallery in EDIT mode
  const [gallerySaving, setGallerySaving] = useState(false);
  const [gallerySaved, setGallerySaved] = useState(false);
  const gallerySaveTimerRef = useRef(null);
  const prevGalleryRef = useRef(null);

  const saveGalleryNow = async (gallery, coverImage) => {
    if (!isEdit || !id) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setGallerySaving(true);
    setGallerySaved(false);
    try {
      await axios.patch(`${API}/kennels/${id}/gallery`, {
        gallery: gallery,
        cover_image: coverImage,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setGallerySaved(true);
      setTimeout(() => setGallerySaved(false), 3000);
    } catch (e) {
      console.error('[KennelWizard] Gallery auto-save failed:', e);
      toast.error('Nie udalo sie zapisac zdjec');
    } finally {
      setGallerySaving(false);
    }
  };

  // Auto-save gallery with debounce when editing
  useEffect(() => {
    if (!isEdit || !id) return;
    const currentGalleryKey = JSON.stringify([form.gallery, form.cover_image]);
    if (prevGalleryRef.current === null) {
      prevGalleryRef.current = currentGalleryKey;
      return;
    }
    if (prevGalleryRef.current === currentGalleryKey) return;
    prevGalleryRef.current = currentGalleryKey;

    if (gallerySaveTimerRef.current) clearTimeout(gallerySaveTimerRef.current);
    gallerySaveTimerRef.current = setTimeout(() => {
      saveGalleryNow(form.gallery, form.cover_image);
    }, 2000);
    return () => { if (gallerySaveTimerRef.current) clearTimeout(gallerySaveTimerRef.current); };
  }, [form.gallery, form.cover_image, isEdit, id]);
  
  // Auto-save draft for new kennels
  const { loadDraft, clearDraft } = useAutosaveDraft(
    isEdit ? null : 'kennel_wizard', 
    isEdit ? null : form
  );

  // Dynamic steps based on industry
  const getStepsForIndustry = () => {
    if (industryConfig.showSpecies) {
      // Hodowla: 8 steps (name, species, location, organization, contact, description, photos, personalization)
      return 8;
    } else {
      // Other industries: 6 steps (name, location, contact, description, photos, personalization)
      return 6;
    }
  };
  const totalSteps = getStepsForIndustry();
  
  // Step names with translations - dynamic based on industry
  const getStepNames = () => {
    if (industryConfig.showSpecies) {
      return {
        1: industryConfig.labels.name.replace('Nazwa ', ''),
        2: t('kennelWizard.step2', 'Gatunek i rasy'),
        3: t('kennelWizard.step3', 'Lokalizacja'),
        4: t('kennelWizard.step4', 'Organizacja'),
        5: t('kennelWizard.step5', 'Kontakt'),
        6: t('kennelWizard.step6', 'Opis'),
        7: t('kennelWizard.step7', 'Zdjęcia'),
        8: 'Personalizacja',
      };
    } else {
      return {
        1: industryConfig.labels.name.replace('Nazwa ', ''),
        2: t('kennelWizard.step3', 'Lokalizacja'),
        3: t('kennelWizard.step5', 'Kontakt'),
        4: t('kennelWizard.step6', 'Opis'),
        5: t('kennelWizard.step7', 'Zdjęcia'),
        6: 'Personalizacja',
      };
    }
  };
  const stepNames = getStepNames();

  // Track wizard start
  useEffect(() => {
    if (!isEdit && user) {
      trackStart({ is_edit: false });
      
      // Load draft for new kennels - only once
      const draft = loadDraft();
      if (draft && draft.data) {
        // Auto-load draft without asking (silent)
        // User can always start fresh by clearing localStorage
        // Removed confirm dialog to improve UX
      }
    }
  }, [user, isEdit]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    } else if (isEdit && user) {
      fetchKennel();
    }
  }, [user, authLoading, isEdit, id, navigate]);

  const fetchKennel = async () => {
    setFetchingData(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/kennels/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Map API response to form fields
      const data = res.data;
      const formData = {
        name: data.name || '',
        species: data.species || [],
        breeds: data.breeds || [],
        postal_code: data.postal_code || '',
        city: data.city || '',
        street: data.street || '',
        house_number: data.house_number || '',
        voivodeship: data.voivodeship || '',
        country: data.country || 'Polska',
        organization: data.organization || '',
        organization_custom: data.organization_custom || '',
        organization_type: data.organization_type || 'unknown',
        is_fci: data.is_fci || false,
        registration_number: data.registration_number || '',
        branch_name: data.branch_name || '',
        founded_year: data.founded_year || '',
        email: data.email || '',
        phone: data.phone || '',
        facebook: data.facebook || '',
        instagram: data.instagram || '',
        website: data.website || '',
        description: data.description || '',
        cover_image: data.cover_image_url || data.cover_image || '',
        gallery: data.gallery_image_urls || data.gallery || [],
        theme: data.theme || 'dark',
        accent_color: data.accent_color || 'gold',
        template_version: data.template_version || 'v1',
        section_order: data.section_order?.length ? data.section_order : DEFAULT_SECTIONS,
        logo: data.logo || '',
        show_in_catalog: Boolean(data.show_in_catalog),
      };
      setForm(formData);
      setOriginalData(formData); // Store original for change tracking
      setTermsAccepted(true); // Already accepted when editing
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error('Sesja wygasła — zaloguj się ponownie');
        localStorage.removeItem('token');
        window.location.href = '/login';
        return;
      }
      console.error('Error fetching kennel:', e);
      toast.error('Nie znaleziono hodowli');
      navigate('/panel');
    } finally {
      setFetchingData(false);
    }
  };

  const updateForm = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const toggleSpecies = (species) => {
    setForm(prev => ({
      ...prev,
      species: prev.species.includes(species)
        ? prev.species.filter(s => s !== species)
        : [...prev.species, species],
      breeds: prev.species.includes(species) ? prev.breeds : [],
    }));
  };

  const toggleBreed = (breed) => {
    setForm(prev => ({
      ...prev,
      breeds: prev.breeds.includes(breed)
        ? prev.breeds.filter(b => b !== breed)
        : [...prev.breeds, breed],
    }));
  };

  const [breedSearch, setBreedSearch] = useState('');
  const [customBreedInput, setCustomBreedInput] = useState('');

  const generateDescription = async () => {
    if (!form.name || form.breeds.length === 0) {
      toast.error('Najpierw wypełnij nazwę i wybierz rasy');
      return;
    }
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/ai/generate`, {
        type: 'kennel_description',
        kennel_id: isEdit ? id : null,
        style: 'premium',
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      updateForm('description', res.data.content);
      toast.success('Opis wygenerowany!');
    } catch (e) {
      toast.error('Błąd generowania opisu');
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async () => {
    // Use CORE schema-based validation for all MUST fields
    const { isValid, errors: mustErrors, firstErrorStep } = coreValidateAllMust(
      'PROFILE', 
      form, 
      kennelProfileSchema
    );
    
    if (!isValid) {
      const missingCount = Object.keys(mustErrors).length;
      toast.error(`Uzupełnij ${missingCount} wymagane pola`);
      trackError('submit', 'validation_failed', { missing_fields: Object.keys(mustErrors) });
      
      // Navigate to first error step
      if (firstErrorStep && firstErrorStep !== step) {
        setStep(firstErrorStep);
        setErrors(mustErrors);
      }
      return;
    }
    
    // Check contact method (soft warning, not blocking for PROFILE)
    if (!hasContactMethod(form)) {
      toast.warning('Zalecamy dodać email lub telefon, żeby klienci mogli się skontaktować');
    }
    
    // Check terms for new kennels
    if (!isEdit && !termsAccepted) {
      toast.error('Zaakceptuj regulamin');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error(t('toasts.sessionExpired'));
        window.location.href = '/login';
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      
      // Debug: log what we're sending
      console.log('[KennelWizard] Submitting form:', {
        gallery_count: form.gallery?.length,
        gallery: form.gallery,
        cover_image: form.cover_image
      });
      
      let kennelId = id;
      let kennelSlug = null;
      
      if (isEdit) {
        const res = await axios.put(`${API}/kennels/${id}`, form, { headers });
        kennelSlug = res.data.slug;
        kennelId = id;
        markAsSaved(); // Mark as saved to disable warning
        firePawConfetti();
        setSavedKennelId(kennelId);
        setSavedKennelSlug(kennelSlug);
        setShowSuccessModal(true);
      } else {
        const res = await axios.post(`${API}/kennels`, form, { headers });
        kennelId = res.data.id;
        kennelSlug = res.data.slug;
        // Track completion
        trackComplete({ kennel_id: kennelId, species: form.species, breeds_count: form.breeds.length });
        markAsSaved(); // Mark as saved
        clearDraft(); // Clear draft after successful save
        firePawConfetti();
        setSavedKennelId(kennelId);
        setSavedKennelSlug(kennelSlug);
        setShowSuccessModal(true);
      }
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error(t('toasts.sessionExpired'));
        localStorage.removeItem('token');
        window.location.href = '/login';
        return;
      }
      const detail = e.response?.data?.detail;
      let errorMsg = t('wizards.saveError');
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMsg = detail.map(err => err.msg || t('wizards.validationError')).join(', ');
      }
      toast.error(errorMsg);
      trackError('submit', 'api_error', { error: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  // Validation with error messages - USING CORE VALIDATION
  const validateStep = () => {
    // Use core schema-based validation
    const { isValid, errors: stepErrors } = coreValidateStep(step, form, kennelProfileSchema);
    
    // Additional check for terms on last step
    if (step === totalSteps && !isEdit && !termsAccepted) {
      stepErrors.terms = t('wizards.validationError');
    }
    
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  // Step validation (for button state) - USING CORE VALIDATION
  const isStepValid = () => {
    const { isValid } = coreValidateStep(step, form, kennelProfileSchema);
    
    // Additional check for terms on last step
    if (step === totalSteps && !isEdit && !termsAccepted) {
      return false;
    }
    
    return isValid;
  };

  // Navigate to step with missing field
  const handleMissingFieldClick = (item) => {
    if (item.step && item.step !== step) {
      setStep(item.step);
      // Focus field after step change
      setTimeout(() => {
        const fieldEl = document.querySelector(`[data-testid="kennel-${item.field}"], [name="${item.field}"]`);
        fieldEl?.focus?.();
        fieldEl?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      const nextStep = step + 1;
      trackStep(`step_${nextStep}`, stepNames[nextStep], { from_step: step });
      setStep(nextStep);
      setErrors({});
    } else {
      trackError(`step_${step}`, 'validation_failed', { errors: Object.keys(errors) });
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const fieldEl = document.querySelector(`[data-testid="kennel-${firstErrorField}"], [name="${firstErrorField}"]`);
        fieldEl?.focus?.();
        fieldEl?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const availableBreeds = [
    ...(form.species.includes('dog') ? DOG_BREEDS : []),
    ...(form.species.includes('cat') ? CAT_BREEDS : []),
  ];

  // Step titles for WizardShell (7 steps)
  const stepTitles = {
    1: { title: t('kennelWizard.kennelNameTitle'), subtitle: t('kennelWizard.kennelNameHint') },
    2: { title: t('kennelWizard.step2'), subtitle: t('kennelWizard.speciesHint') },
    3: { title: t('kennelWizard.step3'), subtitle: t('kennelWizard.locationHint') },
    4: { title: t('kennelWizard.step4'), subtitle: t('kennelWizard.orgHint') },
    5: { title: t('kennelWizard.step5'), subtitle: t('kennelWizard.contactHint') },
    6: { title: t('kennelWizard.step6'), subtitle: t('kennelWizard.descHint') },
    7: { title: t('kennelWizard.step7'), subtitle: t('kennelWizard.photosHint') },
    8: { title: t('kennelWizard.step8'), subtitle: t('kennelWizard.personalizationHint') },
  };

  if (authLoading || fetchingData) {
    return (
      <div className="min-h-screen bg-[#F7F3EE] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[#F47A3B] animate-spin mx-auto mb-4" />
          <p className="text-zinc-500">{isEdit ? t('kennelWizard.loadingKennel') : t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <PanelLayout title={isEdit ? t('kennelWizard.editKennel') : t('kennelWizard.createKennel')}>
      <div className="max-w-3xl mx-auto">
        {/* Unsaved changes indicator */}
        {hasUnsavedChanges && (
          <div className="mb-4 flex items-center gap-2 text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-4 py-2 rounded-lg">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm">{t('kennelWizard.unsavedChanges')}</span>
          </div>
        )}
        
        {/* Progress header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold text-zinc-900">
              {stepTitles[step]?.title || 'Hodowla'}
            </h1>
            <span className="text-sm text-zinc-500">{t('kennelWizard.stepOf', { step, total: totalSteps })}</span>
          </div>
          {stepTitles[step]?.subtitle && (
            <p className="text-zinc-500 text-sm mb-4">{stepTitles[step].subtitle}</p>
          )}
          <Progress value={(step / totalSteps) * 100} className="h-2 bg-zinc-100" />
        </div>

        {/* Quality Score Bar - show after step 0 */}
        {step > 0 && (
          <QualityScoreBar 
            formData={form} 
            onMissingClick={handleMissingFieldClick}
          />
        )}

        <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
        {/* Step 0: Industry Selection (only for new sites) - Dropdown */}
        {step === 0 && !isEdit && (
          <>
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-2xl bg-[#F7931E]/10 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-[#F7931E]" />
                </div>
                <h2 className="text-2xl font-bold text-zinc-900 mb-2">{t('kennelWizard.selectPageType')}</h2>
                <p className="text-zinc-500">{t('kennelWizard.weCustomize')}</p>
              </div>
              
              <div className="max-w-md mx-auto">
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  {t('kennelWizard.industryLabel')}
                </label>
                <select
                  value={industry}
                  onChange={(e) => {
                    setIndustry(e.target.value);
                    updateForm('industry', e.target.value);
                  }}
                  className="w-full h-12 px-4 rounded-xl border-2 border-zinc-200 bg-white text-zinc-900 focus:border-[#F7931E] focus:outline-none transition-colors cursor-pointer"
                  data-testid="industry-select"
                >
                  {Object.values(INDUSTRIES).map((ind) => (
                    <option key={ind.id} value={ind.id}>
                      {ind.emoji} {ind.name}
                    </option>
                  ))}
                </select>
                
                {/* Selected industry description */}
                {industry && INDUSTRIES[industry] && (
                  <p className="mt-3 text-sm text-zinc-500 text-center">
                    {INDUSTRIES[industry].description}
                  </p>
                )}
              </div>
            </div>
            
            <div className="p-6 bg-zinc-50 border-t border-gray-100">
              <button
                onClick={() => setStep(1)}
                className="w-full h-12 bg-[#F7931E] hover:bg-[#E07A0F] text-white font-semibold rounded-xl transition-colors"
                data-testid="industry-next"
              >
                {t('common.next')}
              </button>
            </div>
          </>
        )}

        {/* Step 1: Kennel Name */}
        {step === 1 && (
          <>
            <WizardStep
              icon={Building2}
              title={t('kennelWizard.kennelNameTitle')}
              microcopy={t('kennelWizard.kennelNameHint')}
              aiScoreBoost={15}
            >
              <Input
                value={form.name}
                onChange={(e) => {
                  updateForm('name', e.target.value);
                  if (errors.name) setErrors({...errors, name: null});
                }}
                placeholder={t('kennel.namePlaceholder')}
                className={`h-14 text-lg bg-zinc-50 border-zinc-200 focus:border-[#F47A3B] focus:ring-[#F47A3B]/20 ${errors.name ? 'border-red-500' : ''}`}
                autoFocus
                data-testid="kennel-name"
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-2">{errors.name}</p>
              )}
              <p className="text-xs text-zinc-400 mt-2">
                {t('kennelWizard.kennelNameTip')}
              </p>
            </WizardStep>
            <WizardActions
              onNext={handleNext}
              isFirstStep={true}
              isNextDisabled={!form.name.trim()}
            />
          </>
        )}

        {/* Step 2: Species & Breeds */}
        {step === 2 && (
          <>
            <WizardStep
              icon={Dog}
              title={t('kennelWizard.speciesTitle')}
              microcopy={t('kennelWizard.speciesHint')}
              aiScoreBoost={20}
            >
              <div className="grid grid-cols-2 gap-4 mb-6">
                <button
                  type="button"
                  onClick={() => {
                    toggleSpecies('dog');
                    if (errors.species) setErrors({...errors, species: null});
                  }}
                  className={`p-6 rounded-xl border-2 transition-all ${
                    form.species.includes('dog')
                      ? 'border-[#F47A3B] bg-[#F47A3B]/5'
                      : 'border-zinc-200 hover:border-zinc-300'
                  }`}
                  data-testid="species-dog"
                >
                  <Dog className={`w-10 h-10 mx-auto mb-2 ${form.species.includes('dog') ? 'text-[#F47A3B]' : 'text-zinc-400'}`} />
                  <span className={form.species.includes('dog') ? 'text-zinc-900 font-medium' : 'text-zinc-500'}>{t('species.dog')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    toggleSpecies('cat');
                    if (errors.species) setErrors({...errors, species: null});
                  }}
                  className={`p-6 rounded-xl border-2 transition-all ${
                    form.species.includes('cat')
                      ? 'border-[#F47A3B] bg-[#F47A3B]/5'
                      : 'border-zinc-200 hover:border-zinc-300'
                  }`}
                  data-testid="species-cat"
                >
                  <Cat className={`w-10 h-10 mx-auto mb-2 ${form.species.includes('cat') ? 'text-[#F47A3B]' : 'text-zinc-400'}`} />
                  <span className={form.species.includes('cat') ? 'text-zinc-900 font-medium' : 'text-zinc-500'}>{t('species.cat')}</span>
                </button>
              </div>
              {errors.species && (
                <p className="text-red-500 text-sm mb-4">{errors.species}</p>
              )}

              {form.species.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-zinc-700 mb-3 block">
                    {t('kennelWizard.selectBreeds')} <span className="text-zinc-400">({t('kennelWizard.minOne')})</span>
                  </label>

                  {/* Selected breeds pills */}
                  {form.breeds.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {form.breeds.map(b => (
                        <span key={b} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F47A3B]/10 border border-[#F47A3B]/30 text-xs font-medium text-zinc-800">
                          {b}
                          <button type="button" onClick={() => toggleBreed(b)} className="ml-0.5 hover:text-red-500">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Search input */}
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <input
                      type="text"
                      value={breedSearch}
                      onChange={e => setBreedSearch(e.target.value)}
                      placeholder="Wyszukaj rase..."
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-zinc-200 text-sm outline-none focus:border-[#F47A3B] focus:ring-1 focus:ring-[#F47A3B]/20 transition-colors"
                      data-testid="kennel-breed-search"
                    />
                  </div>

                  {/* Breed checkboxes grid */}
                  <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto p-1">
                    {(breedSearch.trim()
                      ? availableBreeds.filter(b => b.toLowerCase().includes(breedSearch.toLowerCase()))
                      : availableBreeds
                    ).map((breed) => (
                      <label
                        key={breed}
                        className={`flex items-center gap-2 p-2.5 rounded-lg cursor-pointer transition-colors ${
                          form.breeds.includes(breed)
                            ? 'bg-[#F47A3B]/10 border border-[#F47A3B]/30'
                            : 'bg-zinc-50 border border-transparent hover:bg-zinc-100'
                        }`}
                      >
                        <Checkbox
                          checked={form.breeds.includes(breed)}
                          onCheckedChange={() => {
                            toggleBreed(breed);
                            if (errors.breeds) setErrors({...errors, breeds: null});
                          }}
                        />
                        <span className={`text-sm ${form.breeds.includes(breed) ? 'text-zinc-900' : 'text-zinc-600'}`}>
                          {breed}
                        </span>
                      </label>
                    ))}
                  </div>

                  {/* Custom breed entry */}
                  <div className="flex items-center gap-2 mt-3">
                    <input
                      type="text"
                      value={customBreedInput}
                      onChange={e => setCustomBreedInput(e.target.value)}
                      placeholder="Twoja rasa nie jest na liscie? Wpisz..."
                      className="flex-1 px-3 py-2 rounded-xl border border-zinc-200 text-sm outline-none focus:border-[#F47A3B] transition-colors"
                      onKeyDown={e => {
                        if (e.key === 'Enter' && customBreedInput.trim().length >= 2) {
                          e.preventDefault();
                          if (!form.breeds.includes(customBreedInput.trim())) {
                            toggleBreed(customBreedInput.trim());
                          }
                          setCustomBreedInput('');
                        }
                      }}
                      data-testid="kennel-custom-breed-input"
                    />
                    <button
                      type="button"
                      disabled={customBreedInput.trim().length < 2}
                      onClick={() => {
                        if (customBreedInput.trim().length >= 2 && !form.breeds.includes(customBreedInput.trim())) {
                          toggleBreed(customBreedInput.trim());
                          setCustomBreedInput('');
                        }
                      }}
                      className="px-4 py-2 rounded-xl bg-[#F47A3B] text-white text-sm font-semibold hover:bg-[#E06A2B] transition-colors disabled:opacity-40 disabled:cursor-default"
                      data-testid="kennel-add-custom-breed"
                    >
                      Dodaj
                    </button>
                  </div>

                  {errors.breeds && (
                    <p className="text-red-500 text-sm mt-2">{errors.breeds}</p>
                  )}
                </div>
              )}
            </WizardStep>
            <WizardActions
              onBack={handleBack}
              onNext={handleNext}
              isNextDisabled={form.species.length === 0 || form.breeds.length === 0}
            />
          </>
        )}

        {/* Step 3: Location (for hodowla with species), or Step 2 for others */}
        {((industryConfig.showSpecies && step === 3) || (!industryConfig.showSpecies && step === 2)) && (
          <>
            <WizardStep
              icon={MapPin}
              title="Adres hodowli"
              microcopy="Klienci szukają hodowli w swojej okolicy"
              aiScoreBoost={25}
            >
              <div className="space-y-4">
                {/* Kod pocztowy + Miasto w jednym rzędzie */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-sm font-medium text-zinc-700 mb-1.5 block">Kod pocztowy *</label>
                    <Input
                      value={form.postal_code}
                      onChange={(e) => {
                        const formatted = formatPostalCode(e.target.value);
                        updateForm('postal_code', formatted);
                        if (errors.postal_code) setErrors({...errors, postal_code: null});
                        
                        // Auto-detect voivodeship
                        if (isValidPostalCode(formatted)) {
                          const detected = getVoivodeshipFromPostalCode(formatted);
                          if (detected) {
                            updateForm('voivodeship', detected);
                            if (errors.voivodeship) setErrors({...errors, voivodeship: null});
                          }
                        }
                      }}
                      placeholder="00-000"
                      maxLength={6}
                      className={`h-12 bg-zinc-50 border-zinc-200 ${errors.postal_code ? 'border-red-500' : ''}`}
                      data-testid="kennel-postal-code"
                    />
                    {errors.postal_code && (
                      <p className="text-red-500 text-sm mt-1">{errors.postal_code}</p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-zinc-700 mb-1.5 block">Miasto *</label>
                    <Input
                      value={form.city}
                      onChange={(e) => {
                        updateForm('city', e.target.value);
                        if (errors.city) setErrors({...errors, city: null});
                      }}
                      placeholder="np. Warszawa"
                      className={`h-12 bg-zinc-50 border-zinc-200 ${errors.city ? 'border-red-500' : ''}`}
                      data-testid="kennel-city"
                    />
                    {errors.city && (
                      <p className="text-red-500 text-sm mt-1">{errors.city}</p>
                    )}
                  </div>
                </div>
                
                {/* Ulica + Numer */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-zinc-700 mb-1.5 block">
                      Ulica <span className="text-zinc-400">(opcjonalnie)</span>
                    </label>
                    <Input
                      value={form.street}
                      onChange={(e) => updateForm('street', e.target.value)}
                      placeholder="np. Kwiatowa"
                      className="h-12 bg-zinc-50 border-zinc-200"
                      data-testid="kennel-street"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-zinc-700 mb-1.5 block">
                      Nr domu/lok.
                    </label>
                    <Input
                      value={form.house_number}
                      onChange={(e) => updateForm('house_number', e.target.value)}
                      placeholder="np. 15/3"
                      className="h-12 bg-zinc-50 border-zinc-200"
                      data-testid="kennel-house-number"
                    />
                  </div>
                </div>

                {/* Województwo - auto-detected or manual */}
                <div>
                  <label className="text-sm font-medium text-zinc-700 mb-1.5 block">
                    Województwo *
                    {form.voivodeship && isValidPostalCode(form.postal_code) && (
                      <span className="text-emerald-600 text-xs ml-2">✓ wykryte automatycznie</span>
                    )}
                  </label>
                  <Select value={form.voivodeship} onValueChange={(v) => {
                    updateForm('voivodeship', v);
                    if (errors.voivodeship) setErrors({...errors, voivodeship: null});
                  }}>
                    <SelectTrigger className={`h-12 bg-zinc-50 border-zinc-200 ${errors.voivodeship ? 'border-red-500' : ''}`} data-testid="kennel-voivodeship">
                      <SelectValue placeholder="Wybierz województwo..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {voivodeships.map((v) => (
                        <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.voivodeship && (
                    <p className="text-red-500 text-sm mt-1">{errors.voivodeship}</p>
                  )}
                </div>
              </div>
            </WizardStep>
            <WizardActions
              onBack={handleBack}
              onNext={handleNext}
              isNextDisabled={!form.postal_code.trim() || !form.city.trim() || !form.voivodeship}
            />
          </>
        )}

        {/* Step 4: Organization */}
        {step === 4 && (
          <>
            <WizardStep
              icon={Building2}
              title="Organizacja kynologiczna"
              microcopy="Przynależność do organizacji zwiększa wiarygodność"
              aiScoreBoost={15}
            >
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-zinc-700 mb-1.5 block">Organizacja</label>
                  <Select value={form.organization_type} onValueChange={(v) => {
                    updateForm('organization_type', v);
                    const org = organizations.find(o => o.id === v);
                    updateForm('is_fci', org?.fci || false);
                    // Set legacy organization field for backward compat
                    updateForm('organization', org?.label || '');
                    if (v !== 'other') updateForm('organization_custom', '');
                  }}>
                    <SelectTrigger className="h-12 bg-zinc-50 border-zinc-200" data-testid="kennel-organization-type">
                      <SelectValue placeholder="Wybierz organizację..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {organizations.map((org) => (
                        <SelectItem key={org.id} value={org.id}>
                          <span className="flex items-center gap-2">
                            {org.label}
                            {org.fci && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">FCI</span>}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {/* FCI badge */}
                  {form.is_fci && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-blue-700 bg-blue-50 px-3 py-2 rounded-lg">
                      <Shield className="w-4 h-4" />
                      <span className="font-medium">Hodowla zarejestrowana w FCI</span>
                    </div>
                  )}
                </div>
                
                {form.organization_type === 'other' && (
                  <div>
                    <label className="text-sm font-medium text-zinc-700 mb-1.5 block">Nazwa organizacji</label>
                    <Input
                      value={form.organization_custom}
                      onChange={(e) => updateForm('organization_custom', e.target.value)}
                      placeholder="Wpisz nazwę organizacji..."
                      className="h-12 bg-zinc-50 border-zinc-200"
                      data-testid="kennel-organization-custom"
                    />
                  </div>
                )}
                
                <div>
                  <label className="text-sm font-medium text-zinc-700 mb-1.5 block">Nr rejestracyjny hodowli</label>
                  <Input
                    value={form.registration_number}
                    onChange={(e) => updateForm('registration_number', e.target.value)}
                    placeholder="np. FCI 1234/2020"
                    className="h-12 bg-zinc-50 border-zinc-200"
                    data-testid="kennel-registration-number"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-zinc-700 mb-1.5 block">Oddział / sekcja</label>
                    <Input
                      value={form.branch_name}
                      onChange={(e) => updateForm('branch_name', e.target.value)}
                      placeholder="np. Oddział Kraków"
                      className="h-12 bg-zinc-50 border-zinc-200"
                      data-testid="kennel-branch-name"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-zinc-700 mb-1.5 block">Rok założenia</label>
                    <Input
                      type="number"
                      value={form.founded_year}
                      onChange={(e) => updateForm('founded_year', e.target.value ? parseInt(e.target.value) : '')}
                      placeholder="np. 2015"
                      min="1900"
                      max={new Date().getFullYear()}
                      className="h-12 bg-zinc-50 border-zinc-200"
                      data-testid="kennel-founded-year"
                    />
                  </div>
                </div>
                
                <p className="text-xs text-zinc-400">
                  Wszystkie pola opcjonalne — uzupełnij to, co dotyczy Twojej hodowli
                </p>
              </div>
            </WizardStep>
            <WizardActions
              onBack={handleBack}
              onNext={handleNext}
              nextLabel="Dalej (opcjonalne)"
            />
          </>
        )}

        {/* Step 5: Contact Info (for hodowla), or Step 3 for others */}
        {((industryConfig.showSpecies && step === 5) || (!industryConfig.showSpecies && step === 3)) && (
          <>
            <WizardStep
              icon={Phone}
              title="Kontakt"
              microcopy="Dodaj dane, by klienci mogli się z Tobą skontaktować"
              aiScoreBoost={10}
            >
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-zinc-700 mb-1.5 block">Email</label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => updateForm('email', e.target.value)}
                      placeholder="kontakt@hodowla.pl"
                      className="h-11 bg-zinc-50 border-zinc-200"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-zinc-700 mb-1.5 block">Telefon</label>
                    <Input
                      value={form.phone}
                      onChange={(e) => updateForm('phone', e.target.value)}
                      placeholder="+48 123 456 789"
                      className="h-11 bg-zinc-50 border-zinc-200"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-zinc-700 mb-1.5 block">Facebook</label>
                    <Input
                      value={form.facebook}
                      onChange={(e) => updateForm('facebook', e.target.value)}
                      placeholder="facebook.com/..."
                      className="h-11 bg-zinc-50 border-zinc-200"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-zinc-700 mb-1.5 block">Instagram</label>
                    <Input
                      value={form.instagram}
                      onChange={(e) => updateForm('instagram', e.target.value)}
                      placeholder="@hodowla"
                      className="h-11 bg-zinc-50 border-zinc-200"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-700 mb-1.5 block">Strona WWW</label>
                  <Input
                    value={form.website}
                    onChange={(e) => updateForm('website', e.target.value)}
                    placeholder="https://twoja-hodowla.pl"
                    className="h-11 bg-zinc-50 border-zinc-200"
                  />
                </div>
              </div>
            </WizardStep>
            <WizardActions
              onBack={handleBack}
              onNext={handleNext}
              nextLabel="Dalej (opcjonalne)"
            />
          </>
        )}

        {/* Step 6: Description (only for hodowla with 7 steps, or step 4 for others with 5 steps) */}
        {((industryConfig.showSpecies && step === 6) || (!industryConfig.showSpecies && step === 4)) && (
          <>
            <WizardStep
              icon={FileText}
              title="Opis hodowli"
              microcopy="Dobry opis zwiększa widoczność w wyszukiwarkach o 30%"
              aiScoreBoost={30}
              footer={
                <button
                  type="button"
                  onClick={generateDescription}
                  disabled={generating || !form.name || form.breeds.length === 0}
                  className="flex items-center gap-2 text-sm text-[#F47A3B] hover:text-[#E06A2B] disabled:text-zinc-400"
                >
                  {generating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  {generating ? 'Generuję...' : 'Wygeneruj opis przez AI'}
                </button>
              }
            >
              <Textarea
                value={form.description}
                onChange={(e) => updateForm('description', e.target.value)}
                placeholder="Opisz swoją hodowlę - historię, podejście, osiągnięcia..."
                className="min-h-[180px] bg-zinc-50 border-zinc-200 resize-none"
                data-testid="kennel-description"
              />
              <p className="text-xs text-zinc-400">
                Tip: Wspomij o doświadczeniu, badaniach zdrowotnych i osiągnięciach
              </p>
            </WizardStep>
            <WizardActions
              onBack={handleBack}
              onNext={handleNext}
              nextLabel="Dalej (opcjonalne)"
            />
          </>
        )}

        {/* Photos Step (step 7 for hodowla, step 5 for others) — NOT last step anymore */}
        {((industryConfig.showSpecies && step === 7) || (!industryConfig.showSpecies && step === 5)) && (
          <>
            <WizardStep
              icon={ImageIcon}
              title="Zdjęcia"
              microcopy="Profile ze zdjęciami mają 5x więcej zapytań"
              aiScoreBoost={35}
            >
              <ImageUploader
                coverImage={form.cover_image}
                gallery={form.gallery}
                onCoverChange={(url) => updateForm('cover_image', url)}
                onGalleryChange={(urls) => updateForm('gallery', urls)}
                onUploadingChange={setIsUploading}
                maxGalleryImages={10}
              />
              
              {/* Auto-save status + manual save button (edit mode) */}
              {isEdit && (
                <div className="flex items-center justify-between mt-4 p-3 rounded-xl bg-zinc-50 border border-zinc-200">
                  <div className="flex items-center gap-2 text-sm">
                    {gallerySaving ? (
                      <>
                        <Loader2 className="w-4 h-4 text-orange-500 animate-spin" />
                        <span className="text-orange-600 font-medium">Zapisywanie...</span>
                      </>
                    ) : gallerySaved ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        <span className="text-emerald-600 font-medium">Zapisano automatycznie</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4 text-zinc-400" />
                        <span className="text-zinc-500">Zdjecia zapisuja sie automatycznie</span>
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => saveGalleryNow(form.gallery, form.cover_image)}
                    disabled={gallerySaving}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#F47A3B] hover:bg-[#E06A2B] text-white text-sm font-semibold transition-colors disabled:opacity-50"
                    data-testid="save-gallery-btn"
                  >
                    <Save className="w-4 h-4" />
                    Zapisz zdjecia
                  </button>
                </div>
              )}
              
              {isUploading && (
                <div className="flex items-center gap-2 p-3 bg-orange-50 rounded-lg mt-4">
                  <Loader2 className="w-4 h-4 text-orange-500 animate-spin" />
                  <span className="text-sm text-orange-700">Przesyłanie zdjęć...</span>
                </div>
              )}
            </WizardStep>
            <WizardActions
              onBack={handleBack}
              onNext={handleNext}
              isNextDisabled={isUploading}
              nextLabel="Dalej"
            />
          </>
        )}

        {/* Last Step: Personalization + Terms */}
        {step === totalSteps && (
          <>
            <WizardStep
              icon={Palette}
              title="Personalizacja"
              microcopy="Dostosuj wygląd strony do swojego stylu"
            >
              {/* Template selection */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-zinc-900 mb-3">Szablon strony</label>
                <div className="grid grid-cols-2 gap-3">
                  {TEMPLATE_OPTIONS.map(tmpl => (
                    <button
                      key={tmpl.id}
                      type="button"
                      onClick={() => updateForm('template_version', tmpl.id)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        form.template_version === tmpl.id
                          ? 'border-[#F47A3B] bg-[#F47A3B]/5'
                          : 'border-zinc-200 hover:border-zinc-300'
                      }`}
                      data-testid={`template-${tmpl.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {tmpl.id === 'v3' && <Crown className="w-4 h-4 text-[#F47A3B]" />}
                        <span className={`font-medium ${form.template_version === tmpl.id ? 'text-zinc-900' : 'text-zinc-600'}`}>
                          {tmpl.label}
                        </span>
                      </div>
                      <p className="text-xs text-zinc-500">{tmpl.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Theme (only for V3) */}
              {form.template_version === 'v3' && (
                <>
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-zinc-900 mb-3">Motyw</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => updateForm('theme', 'dark')}
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                          form.theme === 'dark' ? 'border-[#F47A3B] bg-gray-900 text-white' : 'border-zinc-200 hover:border-zinc-300'
                        }`}
                        data-testid="theme-dark"
                      >
                        <Moon className="w-5 h-5" />
                        <div className="text-left">
                          <div className="font-medium">Ciemny</div>
                          <div className="text-xs opacity-70">Elegancki, premium</div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => updateForm('theme', 'light')}
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                          form.theme === 'light' ? 'border-[#F47A3B] bg-[#FAFAF8]' : 'border-zinc-200 hover:border-zinc-300'
                        }`}
                        data-testid="theme-light"
                      >
                        <Sun className="w-5 h-5" />
                        <div className="text-left">
                          <div className="font-medium">Jasny</div>
                          <div className="text-xs text-zinc-500">Czysty, minimalistyczny</div>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Accent Color */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-zinc-900 mb-2">Kolor akcentu</label>
                    <p className="text-xs text-zinc-500 mb-3">Klasyczne</p>
                    <div className="grid grid-cols-6 gap-2 mb-3">
                      {ACCENT_COLORS.filter(c => !c.id.startsWith('pastel')).map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => updateForm('accent_color', c.id)}
                          className={`flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 transition-all ${
                            form.accent_color === c.id ? 'border-gray-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'
                          }`}
                          data-testid={`accent-${c.id}`}
                        >
                          <div className="w-7 h-7 rounded-full" style={{ backgroundColor: c.hex }} />
                          <span className="text-[10px] text-zinc-500">{c.label}</span>
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-zinc-500 mb-3">Pastelowe</p>
                    <div className="grid grid-cols-6 gap-2">
                      {ACCENT_COLORS.filter(c => c.id.startsWith('pastel')).map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => updateForm('accent_color', c.id)}
                          className={`flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 transition-all ${
                            form.accent_color === c.id ? 'border-gray-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'
                          }`}
                          data-testid={`accent-${c.id}`}
                        >
                          <div className="w-7 h-7 rounded-full" style={{ backgroundColor: c.hex }} />
                          <span className="text-[10px] text-zinc-500">{c.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Logo */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-zinc-900 mb-1">Logo hodowli</label>
                    <p className="text-xs text-zinc-500 mb-3">Wyświetlane w nawigacji strony (opcjonalne)</p>
                    <ImageUploader
                      coverImage={form.logo}
                      gallery={[]}
                      onCoverChange={(url) => updateForm('logo', url)}
                      onGalleryChange={() => {}}
                      onUploadingChange={setIsUploading}
                      maxGalleryImages={0}
                    />
                  </div>
                </>
              )}

              {/* Moderation info - only for new */}
              {!isEdit && (
                <div className="mt-6">
                  <ModerationInfoBox variant="minimal" />
                </div>
              )}

              {/* Terms checkbox */}
              {!isEdit && (
                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="mt-1 w-4 h-4 rounded border-zinc-300 text-[#F47A3B] focus:ring-[#F47A3B]"
                    />
                    <span className="text-sm text-zinc-700">
                      Oświadczam, że podane informacje są prawdziwe i ponoszę pełną 
                      odpowiedzialność za treść ogłoszenia. Akceptuję{' '}
                      <a href="/regulamin" target="_blank" className="text-[#F47A3B] hover:underline">
                        regulamin
                      </a>.
                    </span>
                  </label>
                </div>
              )}

              {/* Opt-in for public catalog */}
              <div className="mt-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="kennel-catalog-optin-label">
                  <input
                    type="checkbox"
                    checked={form.show_in_catalog}
                    onChange={(e) => updateForm('show_in_catalog', e.target.checked)}
                    className="mt-1 w-5 h-5 rounded border-emerald-400 text-emerald-500 focus:ring-emerald-400"
                    data-testid="kennel-show-in-catalog"
                  />
                  <span className="text-sm text-zinc-700">
                    <strong>Pokaż moją hodowlę w publicznym katalogu ApisAI</strong>
                    <span className="block text-xs text-zinc-500 mt-0.5">
                      Twoja hodowla będzie widoczna pod adresem apisai.pl/hodowle/... — dotrzesz do kupujących szukających szczeniąt Twojej rasy w Google. Bez zaznaczenia hodowla pozostaje prywatna.
                    </span>
                  </span>
                </label>
              </div>
            </WizardStep>
            <WizardActions
              onBack={handleBack}
              onSubmit={handleSubmit}
              isLastStep={true}
              submitLabel={isEdit ? 'Zapisz zmiany' : 'Utwórz hodowlę'}
              isNextDisabled={isUploading || (!isEdit && !termsAccepted)}
              isLoading={loading}
            />
          </>
        )}
        </div>
      </div>

      {/* Success Modal - "Profil gotowy — Zdobądź zapytania" */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full shadow-2xl my-8">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              
              <h2 className="text-2xl font-bold text-zinc-900 mb-2">
                Twój post jest gotowy
              </h2>
              <p className="text-zinc-500">
                Zdobądź pierwsze zapytania już teraz — zajmie to 2 minuty.
              </p>
            </div>

            {/* PRIMARY CTA — Groups */}
            <div className="mb-6">
              <button
                onClick={() => navigate('/panel/grupy')}
                className="w-full py-4 px-6 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white font-bold rounded-xl text-lg shadow-lg shadow-[#8B5CF6]/25 hover:shadow-xl hover:shadow-[#8B5CF6]/30 transition-all flex items-center justify-center gap-2"
                data-testid="success-cta-groups"
              >
                Zdobądź pierwsze zapytania
              </button>
              <p className="text-center text-xs text-zinc-400 mt-2">Gotowy post + grupy + 2 kliknięcia = zapytania od klientów</p>
            </div>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-200" /></div>
              <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-zinc-400">lub kontynuuj budowę profilu</span></div>
            </div>
            
            {/* 3 Action Cards */}
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {/* Card 1: Dodaj miot */}
              <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-xl p-5 border border-pink-200">
                <div className="w-10 h-10 mb-3 rounded-full bg-pink-500 flex items-center justify-center">
                  <Heart className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-zinc-900 mb-2">Dodaj miot</h3>
                <p className="text-sm text-zinc-600 mb-4">
                  Najszybszy sposób, żeby strona zaczęła zbierać zapytania o szczenięta.
                </p>
                <button
                  onClick={() => navigate('/wizard/miot')}
                  className="w-full py-2.5 px-4 bg-pink-500 hover:bg-pink-600 text-white font-medium rounded-lg transition-colors text-sm"
                >
                  Dodaj miot
                </button>
              </div>

              {/* Card 2: Dodaj psa */}
              <div className="bg-gradient-to-br from-orange-50 to-amber-100 rounded-xl p-5 border border-orange-200">
                <div className="w-10 h-10 mb-3 rounded-full bg-[#F47A3B] flex items-center justify-center">
                  <Dog className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-zinc-900 mb-2">Dodaj psa</h3>
                <p className="text-sm text-zinc-600 mb-4">
                  Dodaj psy do swojej hodowli — suczki, reproduktory i inne.
                </p>
                <button
                  onClick={() => navigate(`/wizard/pies/${savedKennelId}?onboarding=1`)}
                  className="w-full py-2.5 px-4 bg-[#F47A3B] hover:bg-[#E06A2B] text-white font-medium rounded-lg transition-colors text-sm"
                >
                  Dodaj psa
                </button>
              </div>

              {/* Card 3: Udostępnij swoją stronę */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 border border-blue-200">
                <div className="w-10 h-10 mb-3 rounded-full bg-blue-500 flex items-center justify-center">
                  <Share2 className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-zinc-900 mb-2">Udostępnij stronę</h3>
                <p className="text-sm text-zinc-600 mb-4">
                  Skopiuj link, pobierz QR albo przygotuj opis pod Facebooka i ogłoszenia.
                </p>
                <button
                  onClick={() => setShowSharePanel(true)}
                  className="w-full py-2.5 px-4 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors text-sm"
                >
                  Udostępnij
                </button>
              </div>
            </div>

            {/* Your Page URL Section + View Button */}
            {savedKennelSlug && (
              <div className="bg-zinc-50 rounded-xl p-4 mb-6">
                <label className="block text-sm font-medium text-zinc-700 mb-3">Twoja strona:</label>
                <div className="flex gap-2">
                  <input 
                    type="text" 
                    readOnly 
                    value={`${window.location.origin}/hodowla/${savedKennelSlug}`}
                    className="flex-1 px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-600"
                  />
                  <button
                    onClick={() => window.open(`/hodowla/${savedKennelSlug}`, '_blank')}
                    className="px-4 py-2 bg-[#F47A3B] hover:bg-[#E06A2B] text-white font-medium rounded-lg transition-colors text-sm flex items-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    Podgląd
                  </button>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/hodowla/${savedKennelSlug}`);
                      toast.success('Link skopiowany!');
                    }}
                    className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-lg transition-colors text-sm flex items-center gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    Kopiuj
                  </button>
                </div>
              </div>
            )}

            {/* Bottom Link */}
            <div className="text-center">
              <button
                onClick={() => navigate('/panel')}
                className="text-zinc-500 hover:text-[#F47A3B] text-sm font-medium transition-colors"
              >
                Przejdź do panelu →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Panel Modal */}
      {showSharePanel && savedKennelSlug && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-zinc-900">Udostępnij swoją stronę</h3>
              <button 
                onClick={() => setShowSharePanel(false)}
                className="p-2 hover:bg-zinc-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-zinc-500" />
              </button>
            </div>
            <ShareActions
              type="kennel"
              slug={savedKennelSlug}
              name={form.name}
              city={form.city}
              description={form.description}
            />
          </div>
        </div>
      )}
    </PanelLayout>
  );
}
